export default function useLocation() {
  const { isProduction } = useUtils()

  const locationCookie = useCookie<UserLocation | undefined>('geoLocationV2', {
    secure: isProduction(),
    maxAge: 315360000, // 10 years
  })

  interface UserLocationState extends UserLocation {
    edge: UserLocation
  }

  // Set the initial state of the location from the cookie if possible
  const state = useState<UserLocationState>('location', () => ({
    city: locationCookie.value?.city ?? '',
    country: locationCookie.value?.country ?? '',
    countryCode: locationCookie.value?.countryCode ?? '',
    latitude: locationCookie.value?.latitude ?? null,
    longitude: locationCookie.value?.longitude ?? null,
    region: locationCookie.value?.region ?? '',
    regionName: locationCookie.value?.regionName ?? '',
    zipcode: locationCookie.value?.zipcode ?? '',
    edge: {
      city: '',
      country: '',
      countryCode: '',
      latitude: null,
      longitude: null,
      region: '',
      regionName: '',
      zipcode: '',
    },
  }))

  const formattedLocation = computed(() => {
    if (!state.value.city || !state.value.region) return 'Unknown location'

    return `${state.value.city}, ${state.value.region}`
  })

  const formattedLocationWithZip = computed(() => {
    if (!state.value.city || !state.value.region || !state.value.zipcode) return 'Unknown location'

    return `${state.value.city}, ${state.value.region} ${state.value.zipcode}`
  })

  // This function is used to set the location from the edge headers.
  function setEdgeLocation(location: Partial<UserLocation>) {
    // Set edge location
    state.value.edge.city = location.city ?? ''
    state.value.edge.country = location.country ?? ''
    state.value.edge.countryCode = location.countryCode ?? ''
    state.value.edge.latitude = location.latitude ?? null
    state.value.edge.longitude = location.longitude ?? null
    state.value.edge.region = location.region ?? ''
    state.value.edge.regionName = location.regionName ?? ''
    state.value.edge.zipcode = location.zipcode ?? ''

    // If we have any of the location values set then we don't want to override the location with the edge values
    if (
      state.value.city ||
      state.value.country ||
      state.value.countryCode ||
      state.value.latitude ||
      state.value.longitude ||
      state.value.region ||
      state.value.regionName ||
      state.value.zipcode
    )
      return

    state.value.city = state.value.edge.city
    state.value.country = state.value.edge.country
    state.value.countryCode = state.value.edge.countryCode
    state.value.latitude = state.value.edge.latitude
    state.value.longitude = state.value.edge.longitude
    state.value.region = state.value.edge.region
    state.value.regionName = state.value.edge.regionName
    state.value.zipcode = state.value.edge.zipcode
  }

  function setLocation(location: Partial<UserLocation>) {
    state.value.city = location.city ?? ''
    state.value.country = location.country ?? ''
    state.value.countryCode = location.countryCode ?? ''
    state.value.region = location.region ?? ''
    state.value.regionName = location.regionName ?? ''
    state.value.zipcode = location.zipcode ?? ''
    state.value.latitude = location.latitude ?? null
    state.value.longitude = location.longitude ?? null

    // Persist location to cookie when set
    locationCookie.value = {
      city: state.value.city,
      country: state.value.country,
      countryCode: state.value.countryCode,
      region: state.value.region,
      regionName: state.value.regionName,
      zipcode: state.value.zipcode,
      latitude: state.value.latitude,
      longitude: state.value.longitude,
    }
  }

  return {
    state,
    formattedLocation,
    formattedLocationWithZip,
    setEdgeLocation,
    setLocation,
  }
}

export interface UserLocation {
  city: string
  country: string
  countryCode: string
  latitude: number | null
  longitude: number | null
  // region is the state abbreviation
  region: string
  // regionName is the state spelled out
  regionName: string
  zipcode: string
}
